import React, { useEffect } from 'react';
import {
    Container,
    Row,
    Col,
    Card,
    Badge,
    Accordion,
    Table,
    Button,
} from 'react-bootstrap';
import { useLocation, Link } from 'react-router-dom';
import { useFeature } from '@/shared/contexts/FeatureFlagContext';
import { useAuth } from '@/shared/contexts/AuthContext';
import { resolveRoles } from '@/shared/utils/previewRoles';
import { applyEntryHref } from '@/shared/utils/positionsAccess';
import useProgramAvailability from '@/shared/hooks/useProgramAvailability';
import { PROGRAM_PAGE_URLS } from '@/shared/utils/programInfo';
import ProgramComingSoon from '@/components/common/ProgramComingSoon';
import '@/features/about/AboutPage.css';
import TeamSection from './team/TeamSection';
import HowToUseSection from './HowToUseSection';
import { badgeTextFor } from '@/shared/utils/badgeText';

// MSU Denver resource links
const MSU_LINKS = {
    main: 'https://www.msudenver.edu',
    tutoring: 'https://www.msudenver.edu/tutoring/',
    stem: 'https://www.msudenver.edu/stem/',
    careers: 'https://www.msudenver.edu/career/',
    studentEmployment: 'https://www.msudenver.edu/student-employment/',
    academicAffairs: 'https://www.msudenver.edu/academic-affairs/',
    clas: 'https://www.msudenver.edu/clas/',
    studentAcademicServices:
        'https://www.msudenver.edu/student-academic-services/',
    c3lab: 'https://msu-denver.github.io/c3-lab/',
    laProgramGoals:
        'https://www.msudenver.edu/learning-assistant-program/#program-goals',
};

// The contact cards, in PROGRAM_DISPLAY_ORDER.
const CONTACT_PROGRAMS = [
    {
        key: 'la',
        label: 'LA',
        badgeBg: '',
        badgeClass: 'badge-la-custom',
        buttonVariant: 'outline-la',
        buttonClass: '',
        topics: 'Questions about Learning Assistants, pedagogy trainings, or STEM support',
    },
    {
        key: 'si',
        label: 'SI',
        badgeBg: 'success',
        badgeClass: '',
        buttonVariant: 'outline-success',
        buttonClass: '',
        topics: 'Questions about Supplemental Instruction, SI Leader positions, or SI session schedules',
    },
    {
        key: 'ta',
        label: 'TA',
        badgeBg: 'primary',
        badgeClass: '',
        buttonVariant: 'outline-primary',
        buttonClass: '',
        topics: 'Questions about TA positions, faculty requests, or employment processing',
    },
];

function AboutPage() {
    const location = useLocation();
    // The Piper and Rowan sentence in the contact section goes with the
    // assistants flag (#391); the rest of the page is unaffected.
    const assistantsEnabled = useFeature('assistants');
    // Invitations to apply for a position, and the promise that training is
    // tracked here, go with the flags that own those workflows.
    const studentApplicationEnabled = useFeature('studentApplication');
    const trainingEnabled = useFeature('training');
    const programActive = useProgramAvailability();
    const comingSoon = (programType) => programActive?.[programType] === false;
    const canApplyFor = (programType) =>
        studentApplicationEnabled && !comingSoon(programType);
    // The apply buttons start where the dashboard's do: Open Positions when
    // hiring is on and the account may open it, else Courses.
    const hiringEnabled = useFeature('hiring');
    const { userProfile, effectiveRoles } = useAuth();
    const applyHref = applyEntryHref({
        hiring: hiringEnabled,
        studentApplication: studentApplicationEnabled,
        roles: resolveRoles({ effectiveRoles, userProfile }),
        fallback: '/courses',
    });

    // Handle hash navigation for deep links
    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.slice(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location]);

    return (
        <div className="about-page">
            {/* Header */}
            <section className="bg-primary text-white py-5">
                <Container>
                    <Row>
                        <Col lg={8}>
                            <h1 className="display-5 fw-bold">
                                About Peer Support Hub
                            </h1>
                            <p className="lead mb-0">
                                Coordinating peer-based instructional support
                                across MSU Denver
                            </p>
                        </Col>
                    </Row>
                </Container>
            </section>

            <HowToUseSection />

            {/* Mission */}
            <section className="py-5">
                <Container>
                    <Row>
                        <Col lg={8} className="mx-auto">
                            <h2 className="fw-bold mb-4">Our Mission</h2>
                            <p className="lead">
                                The Peer Support Hub unifies the management of
                                three distinct but complementary peer support
                                programs at{' '}
                                <a
                                    href={MSU_LINKS.main}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    MSU Denver
                                </a>
                                .
                            </p>
                            <p>
                                {studentApplicationEnabled
                                    ? 'By providing a single platform for students to discover and apply to peer support opportunities, '
                                    : 'By providing a single platform for faculty to request peer support for their courses, '}
                                we streamline the process while respecting the
                                unique needs and autonomy of each program.
                            </p>
                            <p>
                                Each program (Learning Assistants, Supplemental
                                Instruction, and Teaching Assistants) serves
                                different needs and operates on its own
                                timeline. The Hub coordinates these programs
                                without centralizing control, allowing
                                coordinators to maintain their program&apos;s
                                identity while benefiting from shared
                                infrastructure.
                            </p>

                            <Card className="bg-light border-0 mt-4">
                                <Card.Body>
                                    <h3 className="h5 fw-bold">
                                        Key Principles
                                    </h3>
                                    <Row>
                                        <Col md={4} className="mb-3 mb-md-0">
                                            <h4 className="h6 text-primary">
                                                Transparency
                                            </h4>
                                            <p className="small text-muted mb-0">
                                                Clear visibility into
                                                application status and program
                                                decisions
                                            </p>
                                        </Col>
                                        <Col md={4} className="mb-3 mb-md-0">
                                            <h4 className="h6 text-primary">
                                                Equity
                                            </h4>
                                            <p className="small text-muted mb-0">
                                                Fair distribution of peer
                                                support resources across courses
                                            </p>
                                        </Col>
                                        <Col md={4}>
                                            <h4 className="h6 text-primary">
                                                Coordination
                                            </h4>
                                            <p className="small text-muted mb-0">
                                                Unified platform while
                                                preserving program autonomy
                                            </p>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>

                            {/* Why Peer Support Matters */}
                            <Card className="border-0 shadow-sm mt-4">
                                <Card.Body className="p-4">
                                    <h3 className="h5 fw-bold mb-3">
                                        Why Peer Support Matters
                                    </h3>
                                    <p>
                                        Research consistently shows that
                                        peer-assisted learning improves outcomes
                                        for both the students being helped and
                                        the peer supporters themselves. Benefits
                                        include:
                                    </p>
                                    <Row>
                                        <Col md={6}>
                                            <h4 className="h6 text-success">
                                                For Students Receiving Support
                                            </h4>
                                            <ul className="small">
                                                <li>
                                                    Higher course success rates
                                                    and GPAs
                                                </li>
                                                <li>
                                                    Better understanding of
                                                    difficult concepts
                                                </li>
                                                <li>
                                                    Improved study skills and
                                                    strategies
                                                </li>
                                                <li>
                                                    Increased engagement with
                                                    course material
                                                </li>
                                                <li>
                                                    Stronger connections to the
                                                    university community
                                                </li>
                                            </ul>
                                        </Col>
                                        <Col md={6}>
                                            <h4 className="h6 text-primary">
                                                For Peer Supporters
                                            </h4>
                                            <ul className="small">
                                                <li>
                                                    Deeper mastery of subject
                                                    matter
                                                </li>
                                                <li>
                                                    Valuable leadership and
                                                    communication skills
                                                </li>
                                                <li>
                                                    Paid work experience on
                                                    campus
                                                </li>
                                                <li>
                                                    Professional development and
                                                    training
                                                </li>
                                                <li>
                                                    Strong letters of
                                                    recommendation
                                                </li>
                                            </ul>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Programs Detail Section */}
            <section className="py-5" id="programs">
                <Container>
                    <div className="text-center mb-5">
                        <h2 className="fw-bold">The Programs</h2>
                        <p className="text-muted">
                            Learn about each peer support program in detail
                        </p>
                    </div>

                    {/* LA Program */}
                    <Card className="mb-4 border-0 shadow-sm" id="la">
                        <div className="card-header la-header text-white py-3">
                            <h3 className="mb-0">
                                <Badge bg="light" text="dark" className="me-2">
                                    LA
                                </Badge>
                                Learning Assistants
                            </h3>
                        </div>
                        <Card.Body className="p-4">
                            {comingSoon('la') && (
                                <ProgramComingSoon
                                    programType="la"
                                    className="mb-4"
                                />
                            )}
                            <Row>
                                <Col lg={8}>
                                    <h4 className="h5 fw-bold">
                                        LA Program Mission
                                    </h4>
                                    <p className="lead">
                                        The Learning Assistant (LA) Program at
                                        MSU Denver empowers student success
                                        through collaborative peer-to-peer
                                        learning and academic support.
                                    </p>
                                    <p>
                                        We are committed to fostering an
                                        inclusive educational environment in
                                        which undergraduate LAs work alongside
                                        faculty to enhance students&apos;
                                        understanding, build confidence, and
                                        promote active engagement in challenging
                                        STEM courses while developing practical
                                        teaching and mentoring skills.
                                    </p>
                                    <p>
                                        Our program serves MSU Denver&apos;s
                                        diverse student community by:
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>
                                                Enhancing Academic Achievement:
                                            </strong>{' '}
                                            Providing targeted support in
                                            gateway STEM courses through
                                            peer-to-peer mentoring, active
                                            learning, and teaching effective
                                            learning strategies that reinforce
                                            classroom learning and promote
                                            sustained student success.
                                        </li>
                                        <li>
                                            <strong>
                                                Developing Leadership and
                                                Teaching Skills:
                                            </strong>{' '}
                                            Training undergraduate students as
                                            Learning Assistants, equipping them
                                            with pedagogical knowledge,
                                            interpersonal skills,
                                            rapport-building strategies,
                                            problem-solving, and critical
                                            thinking skills that prepare them
                                            for future careers and graduate
                                            studies.
                                        </li>
                                        <li>
                                            <strong>
                                                Promoting Inclusive Excellence:
                                            </strong>{' '}
                                            At no cost to students, Learning
                                            Assistants create welcoming spaces
                                            where students from all backgrounds
                                            can access academic support, build a
                                            sense of belonging in STEM, and
                                            develop the confidence needed to
                                            succeed in their chosen fields.
                                        </li>
                                        <li>
                                            <strong>
                                                Supporting Faculty Innovation:
                                            </strong>{' '}
                                            Collaborating with STEM instructors
                                            to implement evidence-based teaching
                                            strategies and methods, gather
                                            student formative and summative
                                            feedback to continuously improve
                                            course design and delivery methods.
                                        </li>
                                        <li>
                                            <strong>
                                                Building Community Connections:
                                            </strong>{' '}
                                            Strengthening the bonds between
                                            students, faculty, and Learning
                                            Assistants through rapport and a
                                            sense of belonging while fostering a
                                            culture of mutual support, academic
                                            integrity, and lifelong learning.
                                        </li>
                                    </ul>
                                    <p>
                                        Through our commitment to peer learning,
                                        professional development, and
                                        student-centered support, the LA Program
                                        advances MSU Denver&apos;s mission of
                                        providing accessible, no-cost,
                                        high-quality education that transforms
                                        lives and strengthens communities.
                                    </p>

                                    <h4 className="h5 fw-bold mt-4">
                                        Three Essential Elements
                                    </h4>
                                    <p>
                                        Our LA Program is built on three
                                        essential elements:
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Pedagogy Training:</strong>{' '}
                                            Training to develop evidence-based
                                            teaching practices.
                                        </li>
                                        <li>
                                            <strong>
                                                Weekly Prep Sessions:
                                            </strong>{' '}
                                            Weekly meetings between LAs and
                                            their faculty mentor to plan and
                                            refine instructional, motivational,
                                            rapport, sense of belonging, and
                                            learning strategies.
                                        </li>
                                        <li>
                                            <strong>
                                                LA Classroom Practice:
                                            </strong>{' '}
                                            Active engagement of LAs in the
                                            classroom to enhance student
                                            learning.
                                        </li>
                                    </ul>
                                    <p>
                                        Together, faculty mentors and LAs form
                                        an instructional team dedicated to
                                        creating effective and inclusive
                                        learning environments in STEM
                                        disciplines. Our program follows the LA
                                        Model developed at the University of
                                        Colorado Boulder, and we are proud
                                        members of the LA International
                                        Alliance.
                                    </p>

                                    <Card className="border-0 mb-4 la-card-bg">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold la-text">
                                                The LA Difference
                                            </h4>
                                            <p className="small mb-2">
                                                LAs are different from Tutors,
                                                Supplemental Instructors, or
                                                Teaching Assistants in several
                                                important ways:
                                            </p>
                                            <ul className="small mb-0">
                                                <li>
                                                    <strong>
                                                        In-class support:
                                                    </strong>{' '}
                                                    LAs work during lecture, not
                                                    outside of class
                                                </li>
                                                <li>
                                                    <strong>
                                                        Pedagogy training:
                                                    </strong>{' '}
                                                    LAs learn the science of how
                                                    people learn
                                                </li>
                                                <li>
                                                    <strong>
                                                        Active learning focus:
                                                    </strong>{' '}
                                                    LAs facilitate inquiry, not
                                                    lecture
                                                </li>
                                                <li>
                                                    <strong>
                                                        Faculty collaboration:
                                                    </strong>{' '}
                                                    LAs help transform teaching
                                                    practices
                                                </li>
                                            </ul>
                                        </Card.Body>
                                    </Card>

                                    <h4 className="h5 fw-bold mt-4">
                                        Pedagogy Training
                                    </h4>
                                    <p>
                                        A unique aspect of the LA program is the{' '}
                                        <strong>
                                            required pedagogy training.
                                        </strong>{' '}
                                        These trainings teach LAs how students
                                        learn and develop effective facilitation
                                        strategies grounded in educational and
                                        psychological research. All new LAs must
                                        complete the trainings. This is a key
                                        component of the LA model and is what
                                        distinguishes LAs from other peer
                                        support roles. Topics include:
                                    </p>
                                    <Row>
                                        <Col md={6}>
                                            <ul className="small">
                                                <li>
                                                    Learning strategies and
                                                    metacognition
                                                </li>
                                                <li>
                                                    Active learning strategies
                                                </li>
                                                <li>
                                                    Asking effective questions
                                                </li>
                                            </ul>
                                        </Col>
                                        <Col md={6}>
                                            <ul className="small">
                                                <li>
                                                    Supporting diverse learners
                                                </li>
                                                <li>
                                                    Building rapport and sense
                                                    of belonging strategies
                                                </li>
                                            </ul>
                                        </Col>
                                    </Row>

                                    <h4 className="h5 fw-bold mt-4">
                                        LA Responsibilities
                                    </h4>
                                    <ul>
                                        <li>
                                            Attend assigned course to support
                                            student learning and engagement in
                                            the classroom.
                                        </li>
                                        <li>
                                            Lead small-group discussions in the
                                            classroom to deepen understanding of
                                            course concepts and materials.
                                        </li>
                                        <li>
                                            Support students in developing
                                            critical thinking and
                                            problem-solving skills during class
                                            and through office hours held
                                            outside of scheduled class time.
                                        </li>
                                        <li>
                                            Hold regular office hours to review
                                            course content, answer questions,
                                            and promote deeper understanding of
                                            course material.
                                        </li>
                                        <li>
                                            Meet weekly with the faculty mentor
                                            to discuss classroom observations
                                            and strategies for supporting
                                            student learning.
                                        </li>
                                        <li>
                                            Note: LAs do not grade student
                                            assignments/assessments.
                                        </li>
                                    </ul>

                                    <h4 className="h5 fw-bold mt-4">
                                        LA Qualifications
                                    </h4>
                                    <ul>
                                        <li>
                                            Must have previously completed and
                                            passed the course they will support
                                            or demonstrate equivalent content
                                            knowledge.
                                        </li>
                                        <li>
                                            Must be in and maintain good
                                            academic standing.
                                        </li>
                                        <li>
                                            Have the time and commitment to
                                            complete the LA pedagogy trainings
                                        </li>
                                        <li>
                                            Interest in STEM education and
                                            helping others learn
                                        </li>
                                        <li>
                                            Strong interpersonal and
                                            communication skills
                                        </li>
                                        <li>
                                            Commitment to working full semester
                                        </li>
                                        <li>
                                            Availability during assigned class
                                            meeting times
                                        </li>
                                        <li>
                                            Ability to work effectively in a
                                            fast-paced classroom environment.
                                        </li>
                                    </ul>

                                    <p className="fw-bold mt-4">
                                        Learn about{' '}
                                        <a
                                            href={MSU_LINKS.laProgramGoals}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            our Program Goals and how they align
                                            with CADRE
                                        </a>
                                        .
                                    </p>

                                    <div className="mt-4 d-flex flex-wrap gap-2">
                                        {canApplyFor('la') && (
                                            <Button
                                                as={Link}
                                                to={applyHref}
                                                className="btn-la-solid text-white border-0"
                                            >
                                                Apply for LA Positions
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-la"
                                            href={PROGRAM_PAGE_URLS.la}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Learn More at MSU Denver
                                            <span className="visually-hidden">
                                                {' '}
                                                (opens in a new tab)
                                            </span>
                                        </Button>
                                        <Button
                                            variant="outline-la"
                                            href={MSU_LINKS.stem}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            MSU Denver STEM Programs
                                        </Button>
                                    </div>
                                </Col>
                                <Col lg={4}>
                                    <Card className="bg-light border-0 mb-3">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold la-text">
                                                Quick Facts
                                            </h4>
                                            <Table size="sm" className="mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Hours/Week
                                                        </td>
                                                        <td>6-10</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Focus
                                                        </td>
                                                        <td>STEM courses</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Training
                                                        </td>
                                                        <td>
                                                            Pedagogy trainings
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Compensation
                                                        </td>
                                                        <td>Hourly wage</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                    <Card className="text-white border-0 la-card-solid">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold">
                                                Career Benefits
                                            </h4>
                                            <p className="small mb-2">
                                                The LA experience is
                                                particularly valuable for
                                                students considering careers in:
                                            </p>
                                            <ul className="small mb-0 ps-3">
                                                <li>
                                                    K-12 or higher education
                                                    teaching
                                                </li>
                                                <li>
                                                    Graduate school (especially
                                                    STEM fields)
                                                </li>
                                                <li>Science communication</li>
                                                <li>
                                                    Training and instructional
                                                    design
                                                </li>
                                            </ul>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* SI Program */}
                    <Card className="mb-4 border-0 shadow-sm" id="si">
                        <div className="card-header bg-success text-white py-3">
                            <h3 className="mb-0">
                                <Badge
                                    bg="light"
                                    text="success"
                                    className="me-2"
                                >
                                    SI
                                </Badge>
                                Supplemental Instruction
                            </h3>
                        </div>
                        <Card.Body className="p-4">
                            {comingSoon('si') && (
                                <ProgramComingSoon
                                    programType="si"
                                    className="mb-4"
                                />
                            )}
                            <Row>
                                <Col lg={8}>
                                    <p className="lead">
                                        SI targets historically difficult
                                        courses, not at-risk students.
                                    </p>
                                    <p>
                                        Supplemental Instruction is an
                                        internationally recognized academic
                                        support model developed at the
                                        University of Missouri-Kansas City.
                                        Unlike tutoring, which focuses on
                                        individual students who seek help, SI
                                        focuses on high-risk courses: those with
                                        historically high rates of D, F, or
                                        withdrawal grades.
                                    </p>
                                    <p>
                                        SI Leaders are students who have
                                        previously excelled in the course and
                                        now attend all class sessions alongside
                                        current students. They don&apos;t
                                        re-teach or lecture; instead, they
                                        facilitate collaborative study sessions
                                        where students work together to master
                                        course content through active learning
                                        techniques.
                                    </p>
                                    <p>
                                        At MSU Denver, SI complements services
                                        offered by the{' '}
                                        <a
                                            href={MSU_LINKS.tutoring}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Tutoring Center
                                        </a>{' '}
                                        by providing course-specific, proactive
                                        support embedded within the classroom
                                        experience.
                                    </p>

                                    <Card className="bg-success bg-opacity-10 border-0 mb-4">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold text-success">
                                                The SI Model: Key Principles
                                            </h4>
                                            <ul className="small mb-0">
                                                <li>
                                                    <strong>
                                                        Proactive, not remedial:
                                                    </strong>{' '}
                                                    SI is offered to all
                                                    students, not just those
                                                    struggling
                                                </li>
                                                <li>
                                                    <strong>
                                                        Course-focused:
                                                    </strong>{' '}
                                                    Sessions integrate course
                                                    content with learning
                                                    strategies
                                                </li>
                                                <li>
                                                    <strong>
                                                        Student-led:
                                                    </strong>{' '}
                                                    SI Leaders facilitate;
                                                    students do the thinking and
                                                    working
                                                </li>
                                                <li>
                                                    <strong>
                                                        Collaborative:
                                                    </strong>{' '}
                                                    Peer learning builds
                                                    community and deeper
                                                    understanding
                                                </li>
                                            </ul>
                                        </Card.Body>
                                    </Card>

                                    <h4 className="h5 fw-bold mt-4">
                                        SI Leader Responsibilities
                                    </h4>
                                    <ul>
                                        <li>
                                            Attend all lectures and take notes
                                            alongside students
                                        </li>
                                        <li>
                                            Plan and facilitate 2-3 study
                                            sessions per week (50 minutes each)
                                        </li>
                                        <li>
                                            Create collaborative learning
                                            activities that integrate content
                                            and study skills
                                        </li>
                                        <li>
                                            Model effective study and
                                            note-taking strategies
                                        </li>
                                        <li>
                                            Track attendance and report to the
                                            SI Coordinator
                                        </li>
                                        <li>
                                            Meet regularly with the SI
                                            Coordinator for supervision
                                        </li>
                                        <li>
                                            Complete required training before
                                            the semester begins
                                        </li>
                                    </ul>

                                    <h4 className="h5 fw-bold mt-4">
                                        Eligibility Requirements
                                    </h4>
                                    <ul>
                                        <li>
                                            Earned an A or B in the target
                                            course
                                        </li>
                                        <li>Cumulative GPA of 2.5 or higher</li>
                                        <li>
                                            Strong communication and
                                            interpersonal skills
                                        </li>
                                        <li>
                                            Faculty recommendation (preferred)
                                        </li>
                                        <li>
                                            Ability to commit 8-12 hours per
                                            week
                                        </li>
                                    </ul>

                                    <div className="mt-4 d-flex flex-wrap gap-2">
                                        {canApplyFor('si') && (
                                            <Button
                                                as={Link}
                                                to={applyHref}
                                                variant="success"
                                            >
                                                Apply for SI Positions
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline-success"
                                            href={PROGRAM_PAGE_URLS.si}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Learn More at MSU Denver
                                            <span className="visually-hidden">
                                                {' '}
                                                (opens in a new tab)
                                            </span>
                                        </Button>
                                    </div>
                                </Col>
                                <Col lg={4}>
                                    <Card className="bg-light border-0 mb-3">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold text-success">
                                                Quick Facts
                                            </h4>
                                            <Table size="sm" className="mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Hours/Week
                                                        </td>
                                                        <td>8-12</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Sessions/Week
                                                        </td>
                                                        <td>2-3</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Training
                                                        </td>
                                                        <td>
                                                            Pre-semester +
                                                            ongoing
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Compensation
                                                        </td>
                                                        <td>Hourly wage</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                    <Card className="bg-success text-white border-0">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold">
                                                Research Shows SI Works
                                            </h4>
                                            <p className="small mb-0">
                                                Students who regularly attend SI
                                                sessions typically earn half to
                                                one full letter grade higher
                                                than students who don&apos;t
                                                attend. SI participation also
                                                correlates with higher retention
                                                and graduation rates.
                                            </p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* TA Program */}
                    <Card className="mb-4 border-0 shadow-sm" id="ta">
                        <div className="card-header bg-primary text-white py-3">
                            <h3 className="mb-0">
                                <Badge
                                    bg="light"
                                    text="primary"
                                    className="me-2"
                                >
                                    TA
                                </Badge>
                                Teaching Assistants
                            </h3>
                        </div>
                        <Card.Body className="p-4">
                            {comingSoon('ta') && (
                                <ProgramComingSoon
                                    programType="ta"
                                    className="mb-4"
                                />
                            )}
                            <Row>
                                <Col lg={8}>
                                    <p className="lead">
                                        Faculty-identified students providing
                                        direct instructional support.
                                    </p>
                                    <p>
                                        The Teaching Assistant program at MSU
                                        Denver connects faculty with qualified
                                        students to provide direct instructional
                                        support. TAs are undergraduate or
                                        graduate students who assist faculty
                                        members with various course-related
                                        responsibilities, from leading lab
                                        sections to holding office hours.
                                    </p>
                                    <p>
                                        Unlike LA and SI, the TA program
                                        operates on a{' '}
                                        <strong>faculty-request model</strong>:
                                        professors identify specific students
                                        they want to work with as TAs. This
                                        ensures TAs are selected by the faculty
                                        who know both the course requirements
                                        and the student&apos;s qualifications.
                                    </p>
                                    <p>
                                        TA positions are processed through{' '}
                                        <a
                                            href={MSU_LINKS.studentEmployment}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            MSU Denver Student Employment
                                        </a>{' '}
                                        and may require additional HR
                                        documentation including I-9 verification
                                        and background checks depending on the
                                        position.
                                    </p>

                                    <Card className="bg-warning bg-opacity-10 border-warning mb-4">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold text-warning">
                                                How TA Requests Work
                                            </h4>
                                            <p className="mb-2 small">
                                                Faculty must{' '}
                                                <strong>
                                                    name a specific student
                                                </strong>{' '}
                                                when requesting a TA. The
                                                request includes:
                                            </p>
                                            <ul className="small mb-0">
                                                <li>
                                                    Student&apos;s full name
                                                </li>
                                                <li>
                                                    MSU Denver email address
                                                </li>
                                                <li>Student ID (900-number)</li>
                                                <li>
                                                    Requested hours per week
                                                </li>
                                                <li>
                                                    Justification and proposed
                                                    responsibilities
                                                </li>
                                            </ul>
                                        </Card.Body>
                                    </Card>

                                    <h4 className="h5 fw-bold mt-4">
                                        Common TA Responsibilities
                                    </h4>
                                    <Row>
                                        <Col md={6}>
                                            <ul className="small">
                                                <li>
                                                    Lead lab sessions or
                                                    recitations
                                                </li>
                                                <li>
                                                    Hold office hours for
                                                    student questions
                                                </li>
                                                <li>
                                                    Grade assignments and
                                                    provide feedback
                                                </li>
                                                <li>
                                                    Prepare course materials
                                                </li>
                                            </ul>
                                        </Col>
                                        <Col md={6}>
                                            <ul className="small">
                                                <li>
                                                    Set up lab equipment and
                                                    demonstrations
                                                </li>
                                                <li>
                                                    Proctor exams when needed
                                                </li>
                                                <li>
                                                    Maintain course resources
                                                    (Canvas, etc.)
                                                </li>
                                                <li>
                                                    Support classroom activities
                                                </li>
                                            </ul>
                                        </Col>
                                    </Row>

                                    <h4 className="h5 fw-bold mt-4">
                                        For Faculty: Requesting a TA
                                    </h4>
                                    {/* The in-Hub steps wait for an explicit
                                        "on": before availability answers, or
                                        when it cannot be read, they would
                                        direct faculty to a request the server
                                        may refuse. */}
                                    {comingSoon('ta') ? (
                                        <p>
                                            This term the TA program takes
                                            faculty requests outside the Peer
                                            Support Hub, through its own call
                                            for applications. See the{' '}
                                            <a
                                                href={PROGRAM_PAGE_URLS.ta}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Teaching Assistant program page
                                                <span className="visually-hidden">
                                                    {' '}
                                                    (opens in a new tab)
                                                </span>
                                            </a>{' '}
                                            for how to request a TA.
                                        </p>
                                    ) : programActive?.ta === true ? (
                                        <ol>
                                            <li>
                                                Identify a qualified student
                                                you&apos;d like as your TA
                                            </li>
                                            <li>
                                                Rank TA on a course support
                                                request in the Hub, naming the
                                                student
                                            </li>
                                            <li>
                                                The TA Coordinator reviews and
                                                processes the request
                                            </li>
                                            <li>
                                                The named student is contacted
                                                to accept the position
                                            </li>
                                            <li>
                                                HR paperwork is completed (I-9,
                                                background check if required)
                                            </li>
                                            <li>
                                                Once processed, the assignment
                                                is finalized
                                            </li>
                                        </ol>
                                    ) : null}

                                    <h4 className="h5 fw-bold mt-4">
                                        For Students: Becoming a TA
                                    </h4>
                                    <p>
                                        Since faculty must initiate TA requests,
                                        students interested in becoming TAs
                                        should:
                                    </p>
                                    <ul className="small">
                                        <li>
                                            Excel in courses where you&apos;d
                                            like to TA
                                        </li>
                                        <li>
                                            Build relationships with faculty
                                            during office hours
                                        </li>
                                        <li>
                                            Express your interest directly to
                                            professors
                                        </li>
                                        <li>
                                            Consider LA or SI as alternative
                                            pathways to peer support
                                        </li>
                                        <li>
                                            Check{' '}
                                            <a
                                                href={MSU_LINKS.careers}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Career Services
                                            </a>{' '}
                                            for posted TA positions
                                        </li>
                                    </ul>
                                    <div className="mt-4">
                                        <Button
                                            variant="outline-primary"
                                            href={PROGRAM_PAGE_URLS.ta}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Learn More at MSU Denver
                                            <span className="visually-hidden">
                                                {' '}
                                                (opens in a new tab)
                                            </span>
                                        </Button>
                                    </div>
                                </Col>
                                <Col lg={4}>
                                    <Card className="bg-light border-0 mb-3">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold text-primary">
                                                Quick Facts
                                            </h4>
                                            <Table size="sm" className="mb-0">
                                                <tbody>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Hours/Week
                                                        </td>
                                                        <td>Varies (5-20)</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Selection
                                                        </td>
                                                        <td>Faculty request</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Training
                                                        </td>
                                                        <td>
                                                            Program-specific
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className="text-muted">
                                                            Compensation
                                                        </td>
                                                        <td>Hourly wage</td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </Card.Body>
                                    </Card>
                                    <Card className="bg-primary text-white border-0">
                                        <Card.Body>
                                            <h4 className="h6 fw-bold">
                                                Benefits of Being a TA
                                            </h4>
                                            <ul className="small mb-0 ps-3">
                                                <li>
                                                    Close mentorship with
                                                    faculty
                                                </li>
                                                <li>
                                                    Deeper understanding of
                                                    course material
                                                </li>
                                                <li>
                                                    Teaching experience for
                                                    graduate school
                                                </li>
                                                <li>
                                                    Strong recommendation
                                                    letters
                                                </li>
                                                <li>Flexible on-campus work</li>
                                            </ul>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Container>
            </section>

            {/* FAQ Section */}
            <section className="bg-light py-5" id="faq">
                <Container>
                    <Row>
                        <Col lg={8} className="mx-auto">
                            <h2 className="fw-bold mb-4 text-center">
                                Frequently Asked Questions
                            </h2>

                            <Accordion>
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>
                                        Can I apply to multiple programs at
                                        once?
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        Yes. A course support application can
                                        ask for more than one program and rank
                                        them in order of preference. Each
                                        program reviews on its own timeline, and
                                        your ranking is honored. Only programs
                                        accepting applications this term can be
                                        selected.
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="1">
                                    <Accordion.Header>
                                        What if I&apos;m accepted by multiple
                                        programs?
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        Your ranked preferences help prevent
                                        this. If your first-choice program
                                        accepts you, any contingent offers from
                                        lower-ranked programs are automatically
                                        voided. If your first choice passes,
                                        your second choice becomes active, and
                                        so on.
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="2">
                                    <Accordion.Header>
                                        How do I become a TA if faculty have to
                                        request me?
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        The TA program requires faculty to
                                        identify specific students. If
                                        you&apos;re interested in being a TA, we
                                        recommend:
                                        <ul className="mb-0 mt-2">
                                            <li>
                                                Excel in courses where
                                                you&apos;d like to TA
                                            </li>
                                            <li>
                                                Build relationships with faculty
                                                during office hours
                                            </li>
                                            <li>
                                                Express your interest directly
                                                to professors
                                            </li>
                                            <li>
                                                Consider LA or SI as alternative
                                                pathways
                                            </li>
                                        </ul>
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="3">
                                    <Accordion.Header>
                                        What training is required?
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        Training varies by program:
                                        <ul className="mb-0 mt-2">
                                            <li>
                                                <strong>LA:</strong> Required
                                                pedagogy trainings for all new
                                                LAs
                                            </li>
                                            <li>
                                                <strong>SI:</strong>{' '}
                                                Pre-semester training plus
                                                ongoing professional development
                                            </li>
                                            <li>
                                                <strong>TA:</strong>{' '}
                                                Program-specific training
                                                coordinated by the TA
                                                Coordinator
                                            </li>
                                        </ul>
                                        {trainingEnabled &&
                                            'All training is tracked through the Hub so you can monitor your progress.'}
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="4">
                                    <Accordion.Header>
                                        How much do peer supporters get paid?
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        All peer support positions are paid
                                        hourly. Exact rates vary by program and
                                        may be adjusted each academic year.
                                        Contact the individual program
                                        coordinators for current pay rates.
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="5">
                                    <Accordion.Header>
                                        Can I support multiple courses?
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        It depends on the program and your
                                        availability. Some experienced peer
                                        supporters do support multiple courses,
                                        but this is determined on a case-by-case
                                        basis by program coordinators based on
                                        demand and your capacity.
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Related MSU Denver Resources */}
            <section className="py-5">
                <Container>
                    <Row className="justify-content-center">
                        <Col lg={10}>
                            <h2 className="fw-bold mb-4 text-center">
                                MSU Denver Resources
                            </h2>
                            <p className="text-center text-muted mb-4">
                                Explore other academic support and career
                                resources at MSU Denver
                            </p>
                            <Row>
                                <Col md={4} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center">
                                            <div className="display-4 mb-2">
                                                📚
                                            </div>
                                            <h3 className="h6">
                                                Tutoring Center
                                            </h3>
                                            <p className="small text-muted">
                                                Free tutoring for most subjects,
                                                writing assistance, and study
                                                skills workshops
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={MSU_LINKS.tutoring}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Visit Tutoring Center
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center">
                                            <div className="display-4 mb-2">
                                                🔬
                                            </div>
                                            <h3 className="h6">
                                                STEM Programs
                                            </h3>
                                            <p className="small text-muted">
                                                STEM scholarships, research
                                                opportunities, and academic
                                                support programs
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={MSU_LINKS.stem}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Explore STEM
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center">
                                            <div className="display-4 mb-2">
                                                💼
                                            </div>
                                            <h3 className="h6">
                                                Career Services
                                            </h3>
                                            <p className="small text-muted">
                                                Resume help, job search
                                                assistance, and professional
                                                development
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={MSU_LINKS.careers}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Career Services
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={4} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center">
                                            <div className="display-4 mb-2">
                                                👔
                                            </div>
                                            <h3 className="h6">
                                                Student Employment
                                            </h3>
                                            <p className="small text-muted">
                                                On-campus jobs, work-study
                                                positions, and employment
                                                resources
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={
                                                    MSU_LINKS.studentEmployment
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Find Jobs
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center">
                                            <div className="display-4 mb-2">
                                                🎓
                                            </div>
                                            <h3 className="h6">
                                                Academic Affairs
                                            </h3>
                                            <p className="small text-muted">
                                                Academic policies, degree
                                                requirements, and curriculum
                                                information
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={MSU_LINKS.academicAffairs}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Academic Affairs
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4} className="mb-3">
                                    <Card className="h-100 border-0 shadow-sm">
                                        <Card.Body className="text-center">
                                            <div className="display-4 mb-2">
                                                🎯
                                            </div>
                                            <h3 className="h6">
                                                Student Academic Services
                                            </h3>
                                            <p className="small text-muted">
                                                Academic advising, tutoring
                                                coordination, and student
                                                success programs
                                            </p>
                                            <Button
                                                variant="outline-primary"
                                                size="sm"
                                                href={
                                                    MSU_LINKS.studentAcademicServices
                                                }
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Academic Services
                                            </Button>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* C3 Lab Section */}
            <section className="bg-light py-5" id="c3lab">
                <Container>
                    <Row className="align-items-center">
                        <Col lg={6} className="mb-4 mb-lg-0">
                            <h3 className="fw-bold mb-3">
                                A C3 Lab Initiative
                            </h3>
                            <p>
                                The Peer Support Hub is developed by the{' '}
                                <strong>
                                    Community-Centered Computing Lab (C3 Lab)
                                </strong>{' '}
                                in the Department of Computer Sciences at
                                Metropolitan State University of Denver.
                            </p>
                            <p>
                                The C3 Lab designs and deploys computing systems
                                that empower communities by improving access to
                                information, strengthening decision-making, and
                                enabling human-centered interaction with data
                                and AI tools.
                            </p>
                            <p className="mb-0">
                                <a
                                    href={MSU_LINKS.c3lab}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-decoration-none"
                                >
                                    Learn more about C3 Lab &rarr;
                                </a>
                            </p>
                        </Col>
                        <Col lg={6}>
                            <Card className="border-0 shadow-sm">
                                <Card.Body className="p-4">
                                    <h4 className="h5 fw-bold mb-3">
                                        Project Leadership
                                    </h4>
                                    <div className="mb-3">
                                        <h5 className="h6 mb-1">
                                            Dr. Daniel Pittman
                                        </h5>
                                        <p className="small text-muted mb-1">
                                            Principal Investigator | C3 Lab
                                            Co-Director
                                        </p>
                                        <p className="small mb-0">
                                            Research: Data Science, Machine
                                            Learning, LLMs, Human-Computer
                                            Interaction
                                        </p>
                                    </div>
                                    <div>
                                        <h5 className="h6 mb-1">
                                            Dr. Ranjidha Rajan
                                        </h5>
                                        <p className="small text-muted mb-1">
                                            Co-Principal Investigator | C3 Lab
                                            Co-Director
                                        </p>
                                        <p className="small mb-0">
                                            Research: AI for Good, Civic
                                            Technology, Human-Centered Computing
                                        </p>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Meet the Team */}
            <TeamSection />

            {/* Contact Section */}
            <section className="py-5">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8}>
                            <h2 className="fw-bold mb-4">Questions?</h2>
                            <p className="text-muted mb-4">
                                Have questions about peer support programs? Each
                                program&apos;s page at MSU Denver has its
                                coordinator&apos;s contact information.
                                {assistantsEnabled &&
                                    ' Our AI assistants, Piper for faculty and Rowan for students, will soon be available to help answer your questions.'}
                            </p>
                            <Row>
                                {CONTACT_PROGRAMS.map((program) => (
                                    <Col
                                        md={4}
                                        className="mb-3"
                                        key={program.key}
                                    >
                                        <Card className="h-100 border-0 shadow-sm">
                                            <Card.Body>
                                                <Badge
                                                    bg={program.badgeBg}
                                                    text={badgeTextFor(
                                                        program.badgeBg
                                                    )}
                                                    className={`mb-2 ${program.badgeClass}`}
                                                >
                                                    {program.label}
                                                </Badge>
                                                <h3 className="h6">
                                                    {program.label} Coordinator
                                                </h3>
                                                <p className="small text-muted mb-2">
                                                    {program.topics}
                                                </p>
                                                <Button
                                                    variant={
                                                        program.buttonVariant
                                                    }
                                                    size="sm"
                                                    href={
                                                        PROGRAM_PAGE_URLS[
                                                            program.key
                                                        ]
                                                    }
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className={
                                                        program.buttonClass
                                                    }
                                                >
                                                    {program.label} Program Page
                                                    <span className="visually-hidden">
                                                        {' '}
                                                        (opens in a new tab)
                                                    </span>
                                                </Button>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
}

export default AboutPage;
